import { ReactElement } from 'react'
import {
  ColDef,
  EditableCallbackParams,
  ICellRendererParams,
  ValueParserParams,
} from 'ag-grid-community'
// Formatters that add symbol suffix/prefix only on the pinned totals row;
// data rows remain plain numeric strings (rendered inside editable pills).
import {
  percentageFormatter,
  pinnedRowCurrencyFormatter,
  pinnedRowPercentageFormatter,
} from 'components/AgGrid/utils/formatters'
import { makeModifiedCellStyle } from 'pages/DecisionEditPage/agGridConfig/modifiedCellStyle'
import type { ApprovalOption } from 'pages/DecisionEditPage/components/ComparisonTabs'
import { ProductLineSummaryTreeNode } from 'ts/interfaces/productLineSummary.interface'
import { createDataPointsValue } from '../../DataPoint/createDataPointsValue'
import DataPointIndicator from '../../DataPoint/DataPointIndicator'
import { validateOgmPercentValue } from '../../validations'

type ProductLineGridContext = {
  isUSDSelected?: boolean
  dealCurrency?: string
  isReadOnly?: boolean
  plCostAvailableSet?: Set<string>
  linesFilter?: string | null
}

const isAllLinesFilter = (linesFilter?: string | null): boolean => linesFilter == null // note: 'all' is already short-circuited via ctx.isReadOnly

const plCostAvailableEditable = (
  params: EditableCallbackParams<ProductLineSummaryTreeNode>
): boolean => {
  const ctx = params.context as ProductLineGridContext | undefined
  if (params.node.group || params.node.rowPinned || ctx?.isUSDSelected || ctx?.isReadOnly)
    return false
  const costSet = ctx?.plCostAvailableSet
  if (!costSet) return isAllLinesFilter(ctx?.linesFilter)
  return costSet.has(params.data?.hierarchyCode ?? '')
}

const plCostAwareRenderer = (
  params: ICellRendererParams<ProductLineSummaryTreeNode>
): string | ReactElement => {
  const ctx = params.context as ProductLineGridContext | undefined
  const display = params.valueFormatted ?? String(params.value ?? '')
  if (params.node?.group || params.node?.rowPinned) {
    return display
  }
  if (ctx?.isUSDSelected || ctx?.isReadOnly) {
    return display
  }
  const costSet = ctx?.plCostAvailableSet
  if (!costSet) {
    return isAllLinesFilter(ctx?.linesFilter) ? (
      <span className="editable-value-pill">{display}</span>
    ) : (
      <span className="readonly-numeric-cell">{display}</span>
    )
  }
  return costSet.has(params.data?.hierarchyCode ?? '') ? (
    <span className="editable-value-pill">{display}</span>
  ) : (
    <span className="readonly-numeric-cell ag-right-aligned-cell">{display}</span>
  )
}

const plCostAwareCellClass = (params: {
  node: { group?: boolean; rowPinned?: string | null }
  context?: ProductLineGridContext
  data?: ProductLineSummaryTreeNode
}): string => {
  if (params.node.group) return 'ag-right-aligned-cell group-numeric-value-cell'
  if (params.context?.isUSDSelected || params.context?.isReadOnly || params.node.rowPinned)
    return 'ag-right-aligned-cell'
  const costSet = params.context?.plCostAvailableSet
  if (!costSet)
    return isAllLinesFilter(params.context?.linesFilter)
      ? 'editable-bordered-cell'
      : 'readonly-numeric-cell-wrapper'
  return costSet.has(params.data?.hierarchyCode ?? '')
    ? 'editable-bordered-cell'
    : 'readonly-numeric-cell-wrapper'
}

type DataPointsCellProps = {
  rowData?: ProductLineSummaryTreeNode
  showRequestedPrice: boolean
}

const HEADER_CHARACTER_WIDTH_PX = 7
const HEADER_HORIZONTAL_PADDING_PX = 36

const getHeaderMinWidth = (headerName?: string, currentWidth?: number): number => {
  const calculatedHeaderWidth = Math.ceil(
    (headerName?.length ?? 0) * HEADER_CHARACTER_WIDTH_PX + HEADER_HORIZONTAL_PADDING_PX
  )

  return Math.max(calculatedHeaderWidth, Number(currentWidth ?? 0))
}

const DataPointsCell = ({ rowData, showRequestedPrice }: DataPointsCellProps) => {
  if (!rowData || rowData.id === 'totals') {
    return null
  }

  const value = createDataPointsValue({
    baseline: rowData.dataPoints,
    ogmOrMargin: rowData.ogmPercent,
    requestedValue: rowData.requestedOgmPercent ?? rowData.ogmPercent,
    isRequestedPriceActive: showRequestedPrice,
  })

  if (!value) {
    return null
  }

  return <DataPointIndicator value={value} />
}

const withMinWidth = <T,>(col: ColDef<T>): ColDef<T> => ({
  ...col,
  minWidth: getHeaderMinWidth(col.headerName, col.width),
  wrapHeaderText: true,
  autoHeaderHeight: true,
})

const getModifiedCellStyle = makeModifiedCellStyle<ProductLineSummaryTreeNode>(
  (data) => data?.hierarchyCode,
  'modifiedPlCodes'
)

const baseProductLineColDefs: ColDef<ProductLineSummaryTreeNode>[] = [
  {
    headerName: 'PL Description',
    field: 'description',
    width: 230,
  },
  {
    headerName: 'Net Revenue',
    field: 'netRevenue',
    width: 160,
    valueFormatter: pinnedRowCurrencyFormatter,
    type: 'numericColumn',
    aggFunc: null,
  },
  {
    headerName: 'Data Points',
    field: 'dataPoints',
    width: 230,
  },
  {
    headerName: 'Total Discount %',
    field: 'totalDiscountPercentUpdated',
    width: 130,
    valueFormatter: pinnedRowPercentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
    editable: plCostAvailableEditable,
    cellEditor: 'agNumberCellEditor',
    valueParser: (params: ValueParserParams) => Number(params.newValue),
    cellRenderer: plCostAwareRenderer,
    cellClass: plCostAwareCellClass,
    cellStyle: getModifiedCellStyle,
  },
  {
    headerName: 'OGM %',
    field: 'ogmPercent',
    width: 75,
    valueFormatter: pinnedRowPercentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
    editable: plCostAvailableEditable,
    cellEditor: 'agNumberCellEditor',
    valueParser: (params: ValueParserParams) => Number(params.newValue),
    valueSetter: (params) => validateOgmPercentValue(params),
    cellRenderer: plCostAwareRenderer,
    cellClass: plCostAwareCellClass,
    cellStyle: getModifiedCellStyle,
  },
  {
    headerName: 'Expert Discount %',
    field: 'expertDiscountPercentage',
    width: 130,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
    hide: true,
  },
  {
    headerName: 'Typical Discount %',
    field: 'typicalDiscountPercentage',
    width: 130,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
    hide: true,
  },
  {
    headerName: 'Floor Discount %',
    field: 'floorDiscountPercentage',
    width: 130,
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
    hide: true,
  },
  {
    colId: 'requested-total-discount-percent',
    headerName: 'Total Discount %',
    field: 'totalDiscountPercent',
    width: 130,
    valueGetter: (params) => (params.node?.rowPinned ? null : params.data?.totalDiscountPercent),
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
    hide: true,
  },
  {
    colId: 'requested-ogm-percent',
    headerName: 'OGM %',
    field: 'requestedOgmPercent',
    width: 75,
    valueGetter: (params) => (params.node?.rowPinned ? null : params.data?.requestedOgmPercent),
    valueFormatter: percentageFormatter,
    type: 'numericColumn',
    aggFunc: null,
    hide: true,
  },
]

export const productLineColDefs: ColDef<ProductLineSummaryTreeNode>[] =
  baseProductLineColDefs.map(withMinWidth)

const buildApprovalColDefs = (
  approvalOption: ApprovalOption
): ColDef<ProductLineSummaryTreeNode>[] => {
  const { id } = approvalOption
  return [
    withMinWidth({
      colId: `approval-${id}-total-discount-percent`,
      headerName: `Total Discount %`,
      width: 130,
      valueGetter: (params) =>
        params.node?.rowPinned
          ? null
          : (params.data?.approvalData?.[id]?.totalDiscountPercentage ?? null),
      valueFormatter: percentageFormatter,
      type: 'numericColumn',
      aggFunc: null,
      hide: true,
    }),
    withMinWidth({
      colId: `approval-${id}-ogm-percent`,
      headerName: `OGM %`,
      width: 75,
      valueGetter: (params) =>
        params.node?.rowPinned ? null : (params.data?.approvalData?.[id]?.ogmPercent ?? null),
      valueFormatter: percentageFormatter,
      type: 'numericColumn',
      aggFunc: null,
      hide: true,
    }),
  ]
}

export const getProductLineColDefs = (
  showRequestedPrice: boolean,
  approvalOptions: ApprovalOption[] = []
): ColDef<ProductLineSummaryTreeNode>[] => {
  const approvalColDefs = approvalOptions.flatMap(buildApprovalColDefs)
  const allCols = [...productLineColDefs, ...approvalColDefs]

  return allCols.map((col) => {
    if (col.field === 'dataPoints') {
      return {
        ...col,
        cellRenderer: (params: { data?: ProductLineSummaryTreeNode }) => (
          <DataPointsCell rowData={params.data} showRequestedPrice={showRequestedPrice} />
        ),
      }
    }

    if ((col.colId || '').startsWith('requested-')) {
      return { ...col, hide: !showRequestedPrice }
    }

    return col
  })
}
